import { html, css, LitElement } from 'lit-element';
import { a11yStyles } from './a11y.js';
import { CONTACT_EMAIL } from './utility.js';
import { t, tHtml, Localized, formatDate } from './i18n.js';

// Public accessibility statement. Unlike the Role Guide this is linked for
// every visitor, signed in or not, and is reachable directly at /accessibility
// (see intrivity_app.js's pathname check).
//
// The wording below is a self-assessment, not an audited claim -- see the
// "Known limitations" and "How this was assessed" sections.
export class IntrivityAccessibility extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            :host {
                display: block;
                font-family: 'Muli', sans-serif;
                color: #222;
                max-width: 820px;
                margin: 0 auto;
                padding: 30px 20px 60px;
            }
            h1 {
                font-size: 1.8em;
                margin: 0 0 6px;
            }
            h2 {
                font-size: 1.15em;
                margin: 36px 0 10px;
                padding-bottom: 6px;
                border-bottom: 1px solid #e2e2e2;
            }
            h3 {
                font-size: 1em;
                margin: 22px 0 6px;
            }
            .lede {
                color: #5c5c5c;
                max-width: 62ch;
                margin: 0 0 30px;
                line-height: 1.5;
            }
            p, li {
                line-height: 1.6;
                color: #444;
                max-width: 70ch;
            }
            ul {
                padding-left: 20px;
            }
            li {
                margin-bottom: 6px;
            }
            .status {
                border: 1px solid #e2e2e2;
                border-left: 5px solid rgb(30, 120, 128);
                border-radius: 6px;
                background: #f7f9f9;
                padding: 16px 18px;
                margin: 0 0 10px;
            }
            .status strong {
                color: rgb(23, 96, 102);
            }
            .caveat {
                border: 1px solid #e6d5a8;
                border-left: 5px solid rgb(166, 97, 0);
                border-radius: 6px;
                background: #fdf8ee;
                padding: 16px 18px;
            }
            dl {
                margin: 0;
            }
            dt {
                font-weight: 700;
                margin-top: 14px;
                color: #222;
            }
            dd {
                margin: 4px 0 0;
                color: #444;
                line-height: 1.6;
                max-width: 70ch;
            }
            a {
                color: rgb(23, 96, 102);
            }
            .meta {
                margin-top: 40px;
                padding-top: 14px;
                border-top: 1px solid #e2e2e2;
                font-size: 0.86em;
                color: #5c5c5c;
            }
        `];
    }

    render() {
        return html`
            <h1>${t('accessibility.title')}</h1>
            <p class="lede">${t('accessibility.lede')}</p>

            <h2>${t('accessibility.statusHeading')}</h2>
            <div class="status">
                <p style="margin:0;">${tHtml('accessibility.status', { url: 'https://www.w3.org/TR/WCAG21/' })}</p>
            </div>

            <h2>${t('accessibility.doneHeading')}</h2>
            <p>${t('accessibility.doneIntro')}</p>
            <ul>
                ${[1, 2, 3, 4, 5, 6].map((n) => html`<li>${t(`accessibility.done${n}`)}</li>`)}
            </ul>

            <h2>${t('accessibility.limitsHeading')}</h2>
            <p>${t('accessibility.limitsIntro')}</p>
            <ul>
                <li>${tHtml('accessibility.limitAt')}</li>
                <li>${tHtml('accessibility.limitPdf')}</li>
                <li>${tHtml('accessibility.limitThirdParty')}</li>
            </ul>

            <h2>${t('accessibility.feedbackHeading')}</h2>
            <div class="caveat">
                <p style="margin:0;">${tHtml('accessibility.feedback', { email: CONTACT_EMAIL })}</p>
            </div>

            <h2>${t('accessibility.compatHeading')}</h2>
            <dl>
                <dt>${t('accessibility.browsersTerm')}</dt>
                <dd>${t('accessibility.browsers')}</dd>
                <dt>${t('accessibility.atTerm')}</dt>
                <dd>${t('accessibility.at')}</dd>
                <dt>${t('accessibility.techTerm')}</dt>
                <dd>${t('accessibility.tech')}</dd>
            </dl>

            <h2>${t('accessibility.assessedHeading')}</h2>
            <p>${t('accessibility.assessed')}</p>

            <p class="meta">${t('accessibility.prepared', { date: formatDate(new Date(2026, 7, 22), { dateStyle: 'long' }) })}</p>
        `;
    }
}

if (!customElements.get('intrivity-accessibility')) customElements.define('intrivity-accessibility', IntrivityAccessibility);
